<script setup lang="ts">
import { INQUIRY_FILTERS, INQUIRY_ITEM, INQUIRY_TYPE, SPOT } from '@/types'
import { matchInquiryFilterToInquiryType } from '@/utils/matchers'
import { sleep } from '@/utils/parsers'
import {
  sendClickedInquiryPreviewEvent,
  sendClickedNextStepsEvent,
} from '@/utils/mixpanel'
import { highlightInquiry } from '@/utils/highlightInquiry'
import { IOS_ERROR } from '@/types'

const router = useRouter()
const route = useRoute()
interface CustomWindow extends Window {
  webkit?: any
}

declare const window: CustomWindow
const authStore = useAuthStore()
const inquiriesStore = useInquiriesStore()
const appStore = useAppStore()

const showSpotSelection = ref(false)
const buttonCollapsed = ref(true)
const newInquiryButton = ref<HTMLElement>()

const showInquiryItemQuickActions = ref(false)
const selectedInquiryItem = ref<INQUIRY_ITEM>()
const showNewInquiryPopUp = ref(false)
const isLoading = ref(false)

const inquiryListContainer = ref<HTMLElement>()
const lastInquiryCount = ref(0)

onMounted(() => {
  lastInquiryCount.value = inquiries.value.length
})

const openSpotSelection = () => {
  showSpotSelection.value = true
}

const spots = ref<SPOT[]>(appStore.spotsForUser ?? [])

const hasSelectedSpot = computed(() => spots.value.some((s) => s.selected))

//for admin user, backend returns empty array
if (!spots.value.length && appStore.spotInfo) {
  spots.value = [
    {
      id: appStore.spotInfo.id,
      name: appStore.spotInfo.name,
      selected: true,
      notificationsEnabled: false,
    },
  ]
}

if (!hasSelectedSpot.value && !appStore.isAdmin && spots.value.length) {
  spots.value[0].selected = true
}

const selectedSpot = computed<SPOT>(() => {
  const spotId = Number(authStore.spotId)
  return (
    spots.value.find((s: SPOT) => s.id == spotId) ?? {
      id: appStore.spotInfo?.id,
      name: appStore.spotInfo?.name,
      selected: true,
      notificationsEnabled: false,
    }
  )
})
const isChangingSpot = ref(false)

const handleSelectedSpot = async (spotId: SPOT['id']) => {
  const deviceToken = localStorage.getItem('device-token')

if (appStore.isAdmin) {
  isChangingSpot.value = true
  authStore.setSpotId(spotId.toString())
  const spotInfoUrl = route.query.device_token
    ? `${endpoints().spotInfo(authStore.spotId)}?device_id=${route.query.device_token}`
    : endpoints().spotInfo(authStore.spotId)

  try {
    const { data, statusCode } = await useApi(spotInfoUrl).json()

    if (statusCode.value === 401 || statusCode.value === 403) {
      const iosError: IOS_ERROR = {
        statusCode: statusCode.value,
        action: 'logout',
      }
      appStore.setIOSError(iosError)
      return
    }

    appStore.setSpotInfo(data.value)
    appStore.setSpotTz(data.value.time_zone ?? 'America/Chicago')
    authStore.setSpotId(data.value.id.toString())
    spots.value = [
      {
        id: data.value.id,
        name: data.value.name,
        selected: true,
        notificationsEnabled: false,
      },
    ]
    showSpotSelection.value = false

    appStore.isLoading = true
    await inquiriesStore.fetchInquiries(
      route.query.inquiryType
        ? (route.query.inquiryType as INQUIRY_TYPE)
        : (matchInquiryFilterToInquiryType(
            appStore.inquiryFilter ?? 'All',
          ) as INQUIRY_TYPE),
      (route.query.filter as INQUIRY_FILTERS['filter']) ?? 'Upcoming',
      (route.query.sort as INQUIRY_FILTERS['sort']) ?? 'Received',
    )
    inquiries.value = inquiriesStore.inquiries
  } finally {
    isChangingSpot.value = false
    appStore.isLoading = false
  }
  return
}

  spots.value.forEach((item) => {
    item.selected = item.id === spotId
  })
  authStore.setSpotId(spotId.toString())

  const spotInfoUrl = deviceToken
    ? `${endpoints().spotInfo(spotId)}?device_id=${deviceToken}`
    : endpoints().spotInfo(spotId)

  const { data, statusCode } = await useApi(spotInfoUrl).json()

  if (statusCode.value === 401 || statusCode.value === 403) {
    const iosError: IOS_ERROR = {
      statusCode: statusCode.value,
      action: 'logout',
    }
    appStore.setIOSError(iosError)
  }

  appStore.setSpotInfo(data.value)
  appStore.setSpotTz(data.value.time_zone ?? 'America/Chicago')

  router.push(`/?spot_id=${spotId}&device_token=${deviceToken}`)
  showSpotSelection.value = false
}

const inquiries = ref<INQUIRY_ITEM[]>([])

const handleFilterInquiries = async (filters: INQUIRY_FILTERS) => {
  isLoading.value = true
  const { inquiryType, filter, sort } = filters
  route.query.inquiryType = inquiryType
  route.query.filter = filter
  route.query.sort = sort

  const { href } = router.resolve(route)
  window.history.pushState({}, '', href)

  await inquiriesStore.fetchInquiries(inquiryType, filter, sort)
  inquiries.value = inquiriesStore.inquiries
  await sleep(500)
  isLoading.value = false
}

const handleShowInquiryItemQuickActions = (inquiry: INQUIRY_ITEM) => {
  selectedInquiryItem.value = inquiry
  showInquiryItemQuickActions.value = true
  sendClickedInquiryPreviewEvent(inquiry)
}

const handleShowNewInquiryPopUp = () => {
  showNewInquiryPopUp.value = true
  buttonCollapsed.value = true
}

onClickOutside(newInquiryButton, (event) => {
  if (!buttonCollapsed.value && newInquiryButton.value != event.target) {
    buttonCollapsed.value = true
  }
})

const handleCloseNewInquiryPopUp = async () => {
  showNewInquiryPopUp.value = false
  
  await inquiriesStore.fetchInquiries(
    route.query.inquiryType
      ? (route.query.inquiryType as INQUIRY_TYPE)
      : (matchInquiryFilterToInquiryType(
          appStore.inquiryFilter ?? 'All',
        ) as INQUIRY_TYPE),
    (route.query.filter as INQUIRY_FILTERS['filter']) ?? 'Upcoming',
    (route.query.sort as INQUIRY_FILTERS['sort']) ?? 'Received',
  )
  
  inquiries.value = inquiriesStore.inquiries
}

const handleRouteToInquiryDetails = (inquiry: INQUIRY_ITEM) => {
  route.query.inquiry_id = inquiry.id.toString()
  route.query.spot_id = selectedSpot.value.id.toString()

  const { showRespond, showFollowUp } = highlightInquiry(
    inquiry,
    inquiriesStore,
  )

  if (showRespond) {
    sendClickedNextStepsEvent(inquiry, 'Respond')
  }

  if (showFollowUp) {
    sendClickedNextStepsEvent(inquiry, 'Follow-up')
  }

  if (
    inquiry.get_num_of_unread_emails > 0 ||
    (inquiry.description !== null && inquiry.description !== '')
  ) {
    return router.push({
      path: 'inquiry/messages',
      query: route.query,
    })
  }

  return router.push({
    path: 'inquiry/details',
    query: route.query,
  })
}

const device_token = useStorage('device-token', '')
const handleOpenSettingsPage = (spotId: number) => {
  router.push(`/settings?spot_id=${spotId}&device_token=${device_token.value}`)
}

const showHamburger = computed(() => appStore.spotHopperEmbedded)

const removePaddingIfNoMultipleSpots = computed(() => {
  return spots.value.length === 1 ? '!p-0' : ''
})

watch(
  () => [authStore.spotId, authStore.isAuthenticated] as [string, boolean],
  async ([newSpotId, newIsAuthenticated], oldValue) => {
    if (isChangingSpot.value) return 
    if (appStore.iosError) return
    if (!appStore.spotInfo) return
    if (!appStore.spotHopperEmbedded && !newIsAuthenticated) return

    const oldSpotId = oldValue?.[0]

    if (newSpotId !== oldSpotId && appStore.isAdmin) {
      const spotInfoUrl = route.query.device_token
        ? `${endpoints().spotInfo(newSpotId)}?device_id=${route.query.device_token}`
        : endpoints().spotInfo(newSpotId)

      const { data, statusCode } = await useApi(spotInfoUrl).json()

      if (statusCode.value === 401 || statusCode.value === 403) {
        const iosError: IOS_ERROR = {
          statusCode: statusCode.value,
          action: 'logout',
        }
        appStore.setIOSError(iosError)
        return
      }

      appStore.setSpotInfo(data.value)
      appStore.setSpotTz(data.value.time_zone ?? 'America/Chicago')
      spots.value = [
        {
          id: data.value.id,
          name: data.value.name,
          selected: true,
          notificationsEnabled: false,
        },
      ]
    }

    appStore.isLoading = true

    try {
      await inquiriesStore.fetchInquiries(
        route.query.inquiryType
          ? (route.query.inquiryType as INQUIRY_TYPE)
          : (matchInquiryFilterToInquiryType(
              appStore.inquiryFilter ?? 'All',
            ) as INQUIRY_TYPE),
        (route.query.filter as INQUIRY_FILTERS['filter']) ?? 'Upcoming',
        (route.query.sort as INQUIRY_FILTERS['sort']) ?? 'Received',
      )

      inquiries.value = inquiriesStore.inquiries

      if (route.name == 'index') {
        await sleep(300)
      }
      if (
        window.webkit &&
        window.webkit.messageHandlers &&
        window.webkit.messageHandlers.spotChangeHandler
      ) {
        window.webkit.messageHandlers.spotChangeHandler.postMessage(
          authStore.spotId,
        )
      }
    } catch (error) {
      // window.debugLog('Index.vue: Fetch inuiries', {
      //   statusCode: '',
      //   data: error,
      // })
    } finally {
      appStore.isLoading = false
    }
  },
  { immediate: true },
)

watch(
  () => inquiriesStore.inquiries,
  async (newValue) => {
    if (newValue) {
      const hadNewInquiry = newValue.length > lastInquiryCount.value

      inquiries.value = newValue
      lastInquiryCount.value = newValue.length

      if (hadNewInquiry && inquiryListContainer.value) {
        await nextTick()
        if (inquiryListContainer.value) {
          inquiryListContainer.value.scrollTop = 0
        }
      }
    }
  },
)

watch(
  () => appStore.spotsForUser,
  async (newValue) => {
    if (newValue) {
      //for admin user, backend returns empty array
      if (!newValue.length) {
        await sleep(300)
        if (appStore.spotInfo) {
          spots.value = [
            {
              id: appStore.spotInfo.id,
              name: appStore.spotInfo.name,
              selected: true,
              notificationsEnabled: false,
            },
          ]
        }
        return
      }

      spots.value = newValue
    }
  },
)
</script>

<template>
  <template v-if="!appStore.isLoading">
    <div class="flex flex-col gap-1">
      <div class="top fixed w-full bg-white shadow-md">
        <AppHeader
          :has-hamburger="showHamburger"
          :has-back-button="false"
          :has-dropdown="!appStore.isAdmin"
          @open-spot-selection="openSpotSelection"
        >
          <template #default>
            <div class="flex w-full justify-between">
              <div v-if="!showHamburger" class="flex w-full items-center gap-1">
                <AppiconLogo class="h-9 w-9" />
                <p class="text-white">
                  {{ selectedSpot?.name ?? appStore.spotInfo?.name }}
                </p>
              </div>
              <div v-else></div>
              <div class="flex max-w-[140px] items-center gap-3">
                <div class="min-w-[100px]">
                  <AppInput
                  v-if="appStore.isAdmin"
                  v-model="selectedSpot.id"
                  class="!text-slate-800"
                  variant="neutral"
                  name="name"
                  type="text"
                  @change="handleSelectedSpot(selectedSpot.id)"
                  ></AppInput>
                </div>
                <div>
                  <AppLogOut v-if="appStore.isAdmin">
                    <AppiconLogOut class="h-5 w-5 fill-white" />
                  </AppLogOut>
                </div>
              </div>
            </div>
          </template>
        </AppHeader>

        <InquiryFilters @update:filters="handleFilterInquiries" />
      </div>

      <div
        v-if="!isLoading"
        ref="inquiryListContainer"
        class="no-scrollbar h-[calc(100dvh-138px)] mt-[136px] overflow-auto transition-opacity duration-300 ease-in-out"
        :class="[
          {
            'pointer-events-none opacity-50': isLoading,
          },
          { '!mt-[160px]': route.query.inquiryType === 'Other Custom' },
        ]"
      >
        <div v-if="inquiries.length">
          <InquiryListItem
            v-for="item in inquiries"
            :key="item.id"
            v-bind="item"
            @on-long-press="handleShowInquiryItemQuickActions"
            @click="handleRouteToInquiryDetails(item)"
          />
        </div>
        <div v-else>
          <p class="pt-4 text-center text-brand-dark">No inquiries.</p>
        </div>
      </div>
      <div v-else class="flex min-h-[80vh] items-center justify-center">
        <AppInlineLoader />
      </div>

      <HuiTransitionRoot
        as="template"
        :show="buttonCollapsed"
        enter="ease-out duration-200"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="ease-in duration-100"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <AppButton
          tabindex="-1"
          class="fixed bottom-4 right-4 h-12 w-12 !shadow-newInq transition-all duration-300 ease-in-out"
          size="md"
          variant="error"
          :is-rounded="true"
          :is-wide="false"
        >
          <AppiconPlus class="fill-white" @click="buttonCollapsed = false" />
        </AppButton>
      </HuiTransitionRoot>

      <HuiTransitionRoot
        enter="ease-out duration-200"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="ease-in duration-100"
        leave-from="opacity-100"
        leave-to="opacity-0"
        as="template"
        :show="!buttonCollapsed"
      >
        <AppButton
          ref="newInquiryButton"
          tabindex="-1"
          class="fixed bottom-4 right-4 !shadow-newInq transition-all duration-300 ease-in-out"
          size="md"
          variant="error"
          :is-rounded="true"
          :is-wide="false"
        >
          <span @click="handleShowNewInquiryPopUp">Add New Inquiry</span>
          <AppiconPlus class="white h-3.5 w-3.5 fill-white" />
        </AppButton>
      </HuiTransitionRoot>
    </div>

    <AppDialog
      :is-open="showSpotSelection"
      :can-close="true"
      :has-close-button="spots.length > 1"
      :header-classes="removePaddingIfNoMultipleSpots"
      @close="showSpotSelection = false"
    >
      <template v-if="spots.length > 1" #title>
        <p class="text-gray-500">Choose Your Spot</p>
      </template>
      <template #content>
        <div v-if="spots.length > 1">
          <AppList
            v-for="spot in spots"
            :key="spot.id"
            name="spot"
            :checked="spot.selected"
            :label="spot.name"
            :spot="spot"
            @selected="handleSelectedSpot(spot.id)"
          />
        </div>
        <div
          class="flex w-full items-center justify-center gap-1 bg-brand-dark p-4 text-white"
          @click="handleOpenSettingsPage(selectedSpot.id)"
        >
          <AppiconBell class="h-5 w-5 fill-white" />
          <p>Settings & Notifications</p>
        </div>
      </template>
    </AppDialog>

    <InquiryItemQuickActions
      v-if="selectedInquiryItem"
      :inquiry="selectedInquiryItem"
      :show="showInquiryItemQuickActions"
      @close="showInquiryItemQuickActions = false"
    />

    <InquiryDrawer
      :show="showNewInquiryPopUp"
      @close="handleCloseNewInquiryPopUp"
    >
      <template #title>
        <div class="text-center">
          <p class="font-bold text-gray-500">New Inquiry</p>
        </div>
      </template>
      <template #content>
        <InquiryItem @close="handleCloseNewInquiryPopUp" />
      </template>
    </InquiryDrawer>
  </template>
  <AppLoader v-else />
</template>